/**
 * Standard Metric Thresholds for Conditional Formatting
 * Used across the application for color-coding performance indicators
 */

export const METRIC_THRESHOLDS = {
  // Net Margin thresholds
  netMargin: {
    excellent: 15,  // >= 15% is excellent (green)
    good: 12,       // >= 12% is good (light green)
    warning: 10,    // >= 10% is warning (yellow)
    poor: 8,        // < 8% is poor (red)
  },
  
  // Gross Profit Margin (GPM) thresholds
  gpm: {
    excellent: 25,  // >= 25% is excellent
    good: 22,       // >= 22% is good
    warning: 20,    // >= 20% is warning
    poor: 18,       // < 18% is poor
  },
  
  // Take Rate thresholds
  takeRate: {
    high: 2.8,      // > 2.8% may be too high
    excellent: 2.5, // 2.3-2.5% is excellent
    goodHigh: 2.3,
    goodLow: 2.0,   // 2.0-2.3% is good
    warning: 1.8,   // 1.8-2.0% is warning
    poor: 1.8,      // < 1.8% is poor
  },
  
  // Cost Ratio thresholds (% of gross fees)
  costRatio: {
    excellent: 10,  // <= 10% is excellent
    good: 12,       // <= 12% is good
    warning: 15,    // <= 15% is warning
    poor: 18,       // > 18% is poor
  },
  
  // Merchant Fee % thresholds
  merchantFeePercent: {
    excellent: 70,  // <= 70% is excellent
    good: 75,       // <= 75% is good
    warning: 78,    // <= 78% is warning
    poor: 80,       // > 80% is poor
  },
  
  // Approval Rate thresholds
  approvalRate: {
    excellent: 80,  // >= 80% is excellent
    good: 70,       // >= 70% is good
    warning: 60,    // >= 60% is warning
    poor: 50,       // < 50% is poor
  },
  
  // Approval Time thresholds (hours)
  approvalTime: {
    excellent: 24,  // <= 24h is excellent
    good: 48,       // <= 48h is good
    warning: 72,    // <= 72h is warning
    poor: 96,       // > 96h is poor
  },
};

/**
 * Get color class based on metric value and thresholds
 * @param {number} value - The metric value
 * @param {string} metricType - Type of metric (netMargin, gpm, etc.)
 * @param {boolean} inverse - If true, lower is better (e.g., cost ratio)
 * @returns {object} - { textColor, bgColor, borderColor, badgeColor }
 */
export const getMetricColor = (value, metricType, inverse = false) => {
  const thresholds = METRIC_THRESHOLDS[metricType];
  if (!thresholds) return { textColor: 'text-slate-700', bgColor: 'bg-slate-50', borderColor: 'border-slate-300', badgeColor: 'bg-slate-100' };
  
  let level = 'poor';
  
  // Handle different threshold structures
  if (metricType === 'takeRate') {
    if (value >= thresholds.goodLow && value <= thresholds.goodHigh) {
      level = 'good';
    } else if (value >= thresholds.goodHigh && value <= thresholds.excellent) {
      level = 'excellent';
    } else if (value >= thresholds.warning && value < thresholds.goodLow) {
      level = 'warning';
    } else if (value > thresholds.high) {
      level = 'warning';
    } else {
      level = 'poor';
    }
  } else if (inverse) {
    // Lower is better (cost ratio, merchant fee %, approval time)
    if (value <= thresholds.excellent) level = 'excellent';
    else if (value <= thresholds.good) level = 'good';
    else if (value <= thresholds.warning) level = 'warning';
    else level = 'poor';
  } else {
    // Higher is better (margins, approval rate)
    if (value >= thresholds.excellent) level = 'excellent';
    else if (value >= thresholds.good) level = 'good';
    else if (value >= thresholds.warning) level = 'warning';
    else level = 'poor';
  }
  
  const colors = {
    excellent: {
      textColor: 'text-green-700',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      badgeColor: 'bg-green-100',
      indicator: '🟢'
    },
    good: {
      textColor: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-300',
      badgeColor: 'bg-emerald-100',
      indicator: '🟢'
    },
    warning: {
      textColor: 'text-amber-700',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-300',
      badgeColor: 'bg-amber-100',
      indicator: '🟡'
    },
    poor: {
      textColor: 'text-red-700',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
      badgeColor: 'bg-red-100',
      indicator: '🔴'
    }
  };
  
  return colors[level];
};

/**
 * Get performance label based on metric value
 */
export const getPerformanceLabel = (value, metricType, inverse = false) => {
  const thresholds = METRIC_THRESHOLDS[metricType];
  if (!thresholds) return 'N/A';
  
  if (metricType === 'takeRate') {
    if (value >= thresholds.goodLow && value <= thresholds.excellent) return 'Optimal';
    if (value >= thresholds.warning && value < thresholds.goodLow) return 'Below Target';
    if (value > thresholds.high) return 'Above Market';
    return 'Low';
  } else if (inverse) {
    if (value <= thresholds.excellent) return 'Excellent';
    if (value <= thresholds.good) return 'Good';
    if (value <= thresholds.warning) return 'Fair';
    return 'Needs Improvement';
  } else {
    if (value >= thresholds.excellent) return 'Excellent';
    if (value >= thresholds.good) return 'Good';
    if (value >= thresholds.warning) return 'Fair';
    return 'Needs Improvement';
  }
};
